class GlobalAnnouncement extends HTMLElement {
  constructor(){
    super();

    //identify the ticker, collection and collection items
    this.ticker = this.querySelector('.js-announcement-ticker');
    this.collection = this.querySelector('.js-announcement-collection');
    this.originalCollectionItems = this.collection.querySelectorAll('.js-announcement-item');
    this.readyClass = 'is-ready';
    this.pausedClass = 'is-paused';

    // check if the ticker and collection exist
    if (!this.ticker || !this.collection) {
      return null;
    }

    // set vertical scroll variables
    this.itemsIndex = 1;
    this.scrollY = 0;
    this.flipSpeed = parseFloat(this.dataset.flipSpeed) * 1000 || 0;
    this.pauseSpeed = parseFloat(this.dataset.pauseSpeed) * 1000 || 0;
    this.totalSpeed = this.flipSpeed + this.pauseSpeed;

    this.init();
    
    //set and debounce resize listener
    window.addEventListener("load", (event) => {
      this.initEventListeners();
    });
  }

  initEventListeners() {
    window.addEventListener("resize", debounce(() => {
      this.init();
    }, 200).bind(this));

    // Local function to pause ticker on hover
    function togglePause(element, events, callback) {
      events.forEach(event => element.addEventListener(event, callback));
    }
    // Instantiate the togglePause function. CSS and javascript below will handle the rest.
    togglePause(this.ticker, ["mouseenter", "mouseleave", "touchstart", "touchend"], debounce(() => {
      if (this.ticker.classList.contains(this.pausedClass)) {
        this.ticker.classList.remove(this.pausedClass);
      } else {
        this.ticker.classList.add(this.pausedClass);
      }
    }, 200).bind(this));
  }

  duplicateCollection() {
    //get viewport width
    this.viewportWidth = document.body.clientWidth;
    //identify ticker width
    this.collection.width = this.collection.clientWidth;

    //remove duplicate collections if they exist
    const collectionCount = this.ticker.childElementCount;
    if (collectionCount > 1) {
      for (let i = 0; i < collectionCount - 1; i++) {
        this.ticker.removeChild(this.ticker.lastElementChild);     
      }
    }

    let duplicateIterations = 1;
    //use viewport width to determine how many copies of the ticker item collection to duplicate and insert
    if (this.collection.width && this.viewportWidth > this.collection.width) {
      duplicateIterations = Math.ceil(this.viewportWidth/this.collection.width);
    }

    for (let i = 0; i < duplicateIterations; i++) {
      const dupCollection = this.collection.cloneNode(true);
      dupCollection.setAttribute("aria-hidden", true);
      dupCollection.classList.remove("js-announcement-collection", "item-collection-original");
      this.ticker.appendChild(dupCollection);   
    }
  }

  setWidthStyleVar(){
    //set ticker width custom property
    if (this.collection.width) {
      this?.style.setProperty('--logo-ticker-width', `-${this.collection.width}px`);
    }
  }

  setAnimDurationVar(){
    //set ticker animations duration custom property to ensure consistent speeds
    if (this.collection.width) {
      //determine the collection width's percentage of the viewport width at the most common screen resolution (1920 x 1080)
      const durationFactor = this.collection.width/1920;
      //get the animation duration as applied by the section settings
      const baseDuration = this.animTimeProp;
      //multiply base animation duration by percentage above var
      const newDuration = Math.ceil(baseDuration * durationFactor);
      // assign result to custom prop
      this?.style.setProperty('--logo-ticker-time', `${newDuration}s`);
      // start ticker
      this.ticker.classList.add(this.readyClass);
    }
  }

  initTicker() {
    //identify base animation duration
    this.animTimeProp = parseFloat(this.dataset.speed);
    //set width
    this.setWidthStyleVar(); 
    //set animation duration
    this.setAnimDurationVar();
  }

  resetVerticalScroll(hardReset = false) {
    this.ticker.style.setProperty('transition', 'none');
    this.ticker.style.setProperty('transform', 'translate3d(0, 0, 0)');
    this.ticker.style.setProperty('--vertical-scroll-Y', `0%`);
    this.itemsIndex = 1;
    this.scrollY = 0;

    // only clear the interval and timeout when necessary, which is during init and resize.
    if (hardReset) {
      clearInterval(this.scrollerInterval);
      clearTimeout(this.scrollerTimeout);
    }
  }

  initVerticalScroll() {
    this.scrollerInterval = setInterval(() => {
      if (this.ticker.classList.contains(this.pausedClass)) {
        return;
      }
      this.scrollY = this.itemsIndex * 100;

      // Set the scrolling animation
      this.ticker.style.setProperty('transform', 'translate3d(0, var(--vertical-scroll-Y), 0)');
      this.ticker.style.setProperty('transition', `transform ${this.flipSpeed}ms ease-out`);
      this.ticker.style.setProperty('--vertical-scroll-Y', `-${this.scrollY}%`);
      this.itemsIndex++;

      this.scrollerTimeout = setTimeout(() => {
        // Reset the ticker to the first item when first item in duplication collection is shown
        if (this.itemsIndex > this.collectionItems.length / 2) {
          this.resetVerticalScroll();
        }
      }, this.flipSpeed);
    }, this.totalSpeed);
  }

  init() {
    // reset vertical scroll
    this.resetVerticalScroll(true);
    // reset ticker
    this.ticker.classList.remove(this.readyClass);
    // duplicate collection since both ticker and collection require it
    this.duplicateCollection();

    this.collectionItems = this.querySelectorAll('.js-announcement-item');

    // check to see if the ticker should run or not.
    // based on the animation-name property set in global-announcement.css
    if (getComputedStyle(this.ticker).getPropertyValue('animation-name') === 'ticker') {
      // set horizontal ticker
      this.initTicker();
    } else {
      // set vertical scroll if more than item exists in original collection
      if (this.originalCollectionItems.length > 1) {
        this.initVerticalScroll();
      }
    }
  }
}

customElements.define('global-announcement', GlobalAnnouncement);